import { Link } from 'react-router-dom';
import { ArrowLeft, Gift, Star, Heart, Users, Baby, Briefcase, Calendar, CheckCircle } from 'lucide-react';

export default function RatgeberBesteGeschenke2026() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Die besten Eid Geschenke 2026: Der ultimative Guide für unvergessliche Geschenkideen
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Ein umfassender Ratgeber mit über 50 Geschenkideen für Eid ul Fitr und Eid ul Adha – 
            für jeden Empfänger und jedes Budget.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Eid ist mehr als nur ein Feiertag – es ist eine Zeit der Freude, der Familie und der Großzügigkeit. 
              Das perfekte Geschenk zu finden, kann jedoch eine Herausforderung sein. In diesem umfassenden Guide 
              haben wir die besten Eid Geschenke 2026 zusammengestellt, sortiert nach Empfänger, Budget und Anlass.
            </p>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#4A5D4F]" />
                Inhaltsverzeichnis
              </h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#warum-geschenke" className="hover:underline">1. Warum sind Geschenke zu Eid so wichtig?</a></li>
                <li><a href="#maenner" className="hover:underline">2. Die besten Eid Geschenke für Männer</a></li>
                <li><a href="#frauen" className="hover:underline">3. Die besten Eid Geschenke für Frauen</a></li>
                <li><a href="#kinder" className="hover:underline">4. Die besten Eid Geschenke für Kinder</a></li>
                <li><a href="#familien" className="hover:underline">5. Geschenkideen für die ganze Familie</a></li>
                <li><a href="#budget" className="hover:underline">6. Geschenke nach Budget</a></li>
                <li><a href="#tipps" className="hover:underline">7. Tipps für das perfekte Eid Geschenk</a></li>
                <li><a href="#fazit" className="hover:underline">8. Fazit</a></li>
              </ul>
            </div>

            {/* Section 1 */}
            <section id="warum-geschenke" className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                1. Warum sind Geschenke zu Eid so wichtig?
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Schenken zu Eid hat eine tiefe spirituelle Bedeutung im Islam. Der Prophet Muhammad (Friede sei 
                mit ihm) sagte: "Tauscht Geschenke aus, denn Geschenke entfernen Groll aus den Herzen." Diese Tradition 
                stärkt die Bindungen zwischen Familienmitgliedern, Freunden und Nachbarn.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Zu Eid ul Fitr, dem Fest des Fastenbrechens, feiern wir das Ende des Ramadan. Es ist eine Zeit der 
                Dankbarkeit und Freude. Eid ul Adha, das Opferfest, erinnert an die Hingabe Ibrahims und betont die 
                Werte von Opferbereitschaft und Großzügigkeit.
              </p>
              <p className="text-[#4B5563] leading-relaxed">
                Ein durchdachtes Geschenk zeigt nicht nur Wertschätzung, sondern vermittelt auch die Botschaft: 
                "Du bist mir wichtig." In der muslimischen Gemeinschaft in Deutschland ist es besonders bedeutsam, 
                diese Traditionen zu pflegen und an die nächste Generation weiterzugeben.
              </p>
            </section>

            {/* Section 2 - Männer */}
            <section id="maenner" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Die besten Eid Geschenke für Männer
                </h2>
              </div>
              
              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Parfüm & Düfte</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Orientalische Düfte sind ein klassisches Eid-Geschenk für Männer. Oud (Adlerholz) ist dabei 
                besonders beliebt – sein tiefes, holziges Aroma symbolisiert Eleganz und Tradition. Hochwertige 
                Attar-Öle sind konzentriert und langanhaltend, perfekt für den besonderen Anlass.
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Swiss Arabian Geschenksets</strong> – Luxuriöse Parfüm-Kollektionen ab 60€</li>
                <li><strong>RITUALS Velvet Oudh</strong> – Pflege- und Duftset mit orientalischen Noten</li>
                <li><strong>Arabische Attar-Öle</strong> – Traditionelle, konzentrierte Düfte</li>
                <li><strong>Oud-Räucherstäbchen</strong> – Für ein besonderes Ambiente zu Hause</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Gebetszubehör</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Religiöses Zubehör ist ein bedeutungsvolles Geschenk, das täglich verwendet wird. Eine hochwertige 
                Gebetskette (Tasbih) aus Holz, Stein oder Edelmetall ist sowohl praktisch als auch spirituell wertvoll.
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Tasbih aus Kokosholz oder Olive</strong> – Natürlich und langlebig</li>
                <li><strong>Gebetsteppich-Set</strong> – Mit Kompass und Tragetasche</li>
                <li><strong>Digitaler Gebetszeiten-Wecker</strong> – Praktisch und modern</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Weitere Ideen für Männer</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Hochwertige Armbanduhr</strong> – Zeitloser Klassiker</li>
                <li><strong>Leder-Geldbörse</strong> – Praktisch und elegant</li>
                <li><strong>Islamische Kalligraphie</strong> – Als Kunstwerk für das Büro oder Zuhause</li>
                <li><strong>Bücher über islamische Geschichte</strong> – Für den wissbegierigen Mann</li>
              </ul>
            </section>

            {/* Section 3 - Frauen */}
            <section id="frauen" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Die besten Eid Geschenke für Frauen
                </h2>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Beauty & Pflege</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Verwöhn-Sets sind zu Eid besonders beliebt. Von luxuriösen Parfüms bis zu Spa-Sets – 
                Frauen freuen sich über hochwertige Pflegeprodukte, die sie sich selbst vielleicht nicht kaufen würden.
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Swiss Arabian Parfum-Set für Frauen</strong> – Elegante orientalische Düfte</li>
                <li><strong>RITUALS Home Spa Collection</strong> – Luxuriöse Körperpflege</li>
                <li><strong>Natürliche Henna-Sets</strong> – Für traditionelle Eid-Dekoration</li>
                <li><strong>Bio-Arganöl aus Marokko</strong> – Pflege für Haut und Haar</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Schmuck & Accessoires</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Schmuck ist ein zeitloses Geschenk, das Frauen über Jahre hinweg tragen und in Erinnerung behalten. 
                Ob dezent oder auffällig – es gibt für jeden Geschmack das Richtige.
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Goldkette mit Anhänger</strong> – Elegant und vielseitig</li>
                <li><strong>Armband mit islamischer Kalligraphie</strong> – Bedeutungsvoll und schön</li>
                <li><strong>Hochwertige Hijab-Pins</strong> – Praktisch und dekorativ</li>
                <li><strong>Seiden-Hijab-Set</strong> – Luxuriös und festlich</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Weitere Ideen für Frauen</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Duftkerzen-Set</strong> – Mit Oud, Rose und Amber</li>
                <li><strong>Quran mit Übersetzung</strong> – In eleganter Geschenkbox</li>
                <li><strong>Handtasche</strong> – Praktisch und stilvoll</li>
                <li><strong>Islamische Wandkunst</strong> – Für ein schönes Zuhause</li>
              </ul>
            </section>

            {/* Section 4 - Kinder */}
            <section id="kinder" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Baby className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Die besten Eid Geschenke für Kinder
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Für Kinder ist Eid ein magischer Tag voller Vorfreude. Das richtige Geschenk macht diesen Tag 
                unvergesslich und vermittelt gleichzeitig die Bedeutung des Festes.
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Bücher & Lernspiele</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>"Mama, was ist Ramadan?"</strong> – Liebevolles Kinderbuch für 3-8 Jahre</li>
                <li><strong>SIRA Quiz-Karten</strong> – Spielerisch über den Propheten lernen</li>
                <li><strong>Islamische Malbücher</strong> – Kreativ und lehrreich</li>
                <li><strong>Arabisches Alphabet-Puzzle</strong> – Spielend Arabisch lernen</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Spielzeug & Überraschungen</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Eid-Geschenkbox</strong> – Gefüllt mit Süßigkeiten und kleinen Überraschungen</li>
                <li><strong>Plüschtiere</strong> – Kuschelige Begleiter</li>
                <li><strong>Bastelsets</strong> – Eid-Karten und Dekorationen selbst gestalten</li>
                <li><strong>Spiele für die Familie</strong> – Gemeinsame Zeit am Eid-Tag</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Geld & Gutscheine (Eidi)</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                "Eidi" – das traditionelle Geldgeschenk – ist besonders bei älteren Kindern beliebt. 
                Es lehrt den Umgang mit Geld und gibt Kindern die Freiheit, sich selbst etwas auszusuchen.
              </p>
            </section>

            {/* Section 5 - Familien */}
            <section id="familien" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Geschenkideen für die ganze Familie
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Manche Geschenke sind am schönsten, wenn sie von der ganzen Familie genossen werden können. 
                Hier sind unsere Favoriten für gemeinsame Eid-Momente:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kulinarische Geschenke</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Premium Dattel-Kollektion</strong> – Medjool, Ajwa und mehr aus verschiedenen Regionen</li>
                <li><strong>Orientalische Süßigkeiten-Box</strong> – Baklava, Ma'amoul, Turkish Delight</li>
                <li><strong>Hochwertiges Tee-Set</strong> – Mit orientalischen Teesorten und Gläsern</li>
                <li><strong>Gewürz-Geschenkbox</strong> – Für authentische orientalische Küche</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Dekoration & Ambiente</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Eid Mubarak Dekorations-Set</strong> – Banner, Ballons, Tischdeko</li>
                <li><strong>Laternen und Lichterketten</strong> – Für festliche Stimmung</li>
                <li><strong>Islamische Wanduhr</strong> – Mit Gebetszeiten-Funktion</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Gemeinsame Erlebnisse</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Familien-Brettspiele</strong> – SIRA Quiz oder islamische Wissensspiele</li>
                <li><strong>Picknick-Set</strong> – Für den Eid-Ausflug im Park</li>
                <li><strong>Koch-Workshop-Gutschein</strong> – Gemeinsam orientalisch kochen lernen</li>
              </ul>
            </section>

            {/* Section 6 - Budget */}
            <section id="budget" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Geschenke nach Budget
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-3">Bis 20€</h3>
                  <ul className="text-sm text-[#4B5563] space-y-2">
                    <li>• Kinderbücher</li>
                    <li>• Tasbih / Gebetskette</li>
                    <li>• Duftkerze</li>
                    <li>• Süßigkeiten-Box</li>
                    <li>• Malbuch-Set</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-3">20€ – 50€</h3>
                  <ul className="text-sm text-[#4B5563] space-y-2">
                    <li>• Schmuck-Sets</li>
                    <li>• Premium Datteln</li>
                    <li>• Gebetsteppich</li>
                    <li>• Parfüm (kleine Größe)</li>
                    <li>• Bastel- & Spielsets</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-3">Über 50€</h3>
                  <ul className="text-sm text-[#4B5563] space-y-2">
                    <li>• Parfüm-Geschenksets</li>
                    <li>• RITUALS Sets</li>
                    <li>• Hochwertige Uhren</li>
                    <li>• Seiden-Hijab-Sets</li>
                    <li>• Komplette Geschenkboxen</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 7 - Tipps */}
            <section id="tipps" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Tipps für das perfekte Eid Geschenk
                </h2>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">1</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Früh bestellen</h4>
                    <p className="text-[#4B5563]">Bestellen Sie mindestens 1-2 Wochen vor Eid, um Lieferverzögerungen zu vermeiden.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">2</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Persönlich verpacken</h4>
                    <p className="text-[#4B5563]">Eine schöne Verpackung mit handgeschriebener Karte macht jedes Geschenk besonders.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">3</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">An die Interessen denken</h4>
                    <p className="text-[#4B5563]">Das beste Geschenk zeigt, dass Sie den Empfänger kennen und schätzen.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">4</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Qualität vor Quantität</h4>
                    <p className="text-[#4B5563]">Ein hochwertiges Geschenk ist wertvoller als viele kleine, unpersönliche Dinge.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">5</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Niemanden vergessen</h4>
                    <p className="text-[#4B5563]">Erstellen Sie eine Liste aller Personen, denen Sie zu Eid etwas schenken möchten.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8 - Fazit */}
            <section id="fazit" className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                8. Fazit: Das perfekte Eid Geschenk finden
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Schenken zu Eid ist eine wunderbare Tradition, die Herzen verbindet und Freude bereitet. 
                Ob Sie ein luxuriöses Parfüm-Set, ein bedeutungsvolles Buch oder eine süße Überraschung für 
                die Kleinen wählen – das Wichtigste ist die Intention dahinter.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Wir hoffen, dass dieser Guide Ihnen hilft, das perfekte Geschenk für Ihre Liebsten zu finden. 
                Stöbern Sie durch unsere handverlesene Kollektion und machen Sie dieses Eid zu etwas Besonderem.
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Eid Mubarak! 🌙
              </p>
            </section>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Entdecken Sie unsere Geschenkauswahl
              </h3>
              <p className="text-white/80 mb-6">
                Handverlesene Eid Geschenke für jeden Empfänger und jedes Budget
              </p>
              <Link
                to="/geschenke"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Jetzt stöbern
                <Gift className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
